import { getNote } from '../db'
import { getExportMarkdown, getExportPlainText } from './contentPipeline'
import { downloadBlob, sanitizeFilename } from './download'
import { track, AnalyticsEvent } from '../analytics'

export type ExportFormat =
  | 'txt'
  | 'markdown'
  | 'html'
  | 'rtf'
  | 'epub'
  | 'textpack'

/**
 * Every export format produces a downloadable Blob, so single-note download and
 * bulk ZIP share one `getExportBlob()` path with no per-format special-casing.
 */
export type BlobExportFormat = ExportFormat

const FORMAT_EXT: Record<BlobExportFormat, string> = {
  txt: 'txt',
  markdown: 'md',
  html: 'html',
  rtf: 'rtf',
  epub: 'epub',
  textpack: 'textpack',
}

/**
 * Generate the export blob for a single note's content.
 * Shared by single-note export and bulk (ZIP) export.
 */
export async function getExportBlob(
  content: string,
  title: string,
  format: BlobExportFormat,
): Promise<{ blob: Blob; ext: string }> {
  const ext = FORMAT_EXT[format]

  switch (format) {
    case 'txt': {
      const markdown = getExportMarkdown(content)
      const plainText = getExportPlainText(markdown)
      return { blob: new Blob([plainText], { type: 'text/plain;charset=utf-8' }), ext }
    }
    case 'markdown': {
      const markdown = getExportMarkdown(content)
      return { blob: new Blob([markdown], { type: 'text/markdown;charset=utf-8' }), ext }
    }
    case 'html': {
      const { renderNoteHtml } = await import('./exportHtml')
      const fullHtml = await renderNoteHtml(content, title)
      return { blob: new Blob([fullHtml], { type: 'text/html;charset=utf-8' }), ext }
    }
    case 'rtf': {
      const { exportAsRtfBlob } = await import('./exportRtf')
      const blob = await exportAsRtfBlob(content, title)
      return { blob, ext }
    }
    case 'epub': {
      const { exportAsEpubBlob } = await import('./exportEpub')
      const blob = await exportAsEpubBlob(content, title)
      return { blob, ext }
    }
    case 'textpack': {
      const { exportAsTextpackBlob } = await import('./exportTextpack')
      const blob = await exportAsTextpackBlob(content, title)
      return { blob, ext }
    }
  }
}

export async function exportNote(
  noteId: string,
  format: ExportFormat,
): Promise<void> {
  const note = getNote(noteId)
  if (!note) throw new Error('Note not found')

  await exportNoteContent(note.content, note.title, format)
}

/**
 * Export raw content directly — used for shared-with-me notes, whose content
 * lives in the shared_notes cache rather than the notes table.
 */
export async function exportNoteContent(
  content: string,
  title: string,
  format: ExportFormat,
): Promise<void> {
  const { blob, ext } = await getExportBlob(content, title, format)
  downloadBlob(blob, sanitizeFilename(title) + '.' + ext)
  track(AnalyticsEvent.NoteExported, { format, scope: 'single' })
}
